import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import { fabric } from 'fabric';
import { MarkupTool, MarkupEditorConfig, MarkupSaveResult, StrokeSize } from './markup-editor.types';
import { Markup } from '../../models/markup';
import { MarkupConflictError, MarkupExistsError, MarkupLockedError, MarkupService, RedlineBody, RedlineSlot } from '../../services/markup.service';
import { PdfRenderService } from '../../services/pdf-render.service';
import { markupLockCopy, ProjectDrawingsService } from '../../services/project-drawings.service';
import { authorName, MarkBox, markupBBox, recordAsBuiltRows } from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawing-as-built';
import * as pdfjsLib from 'pdfjs-dist';
import { v4 as uuidv4 } from 'uuid';
import {
  bindSheetZoomListeners, SafariPinch, sharpRegion, stepZoom, wheelZoomFactor,
} from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/sheet-zoom-pan';
import {
  clampViewport, FIT_VIEWPORT, FONT_FLOOR_PX, MARKUP_ZOOM_MAX, MARKUP_ZOOMS, panViewport, Viewport, viewportCss, viewportPercent, zoomedSize,
  zoomViewportAt,
} from './markup-zoom';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

// Largest render of a drawing sheet page (and its redline preview), in pixels.
const MAX_SHEET_RENDER_PIXELS = 16_000_000;

/** Key-order-free JSON, for comparing fabric objects the API stored (jsonb reorders keys) with what was sent */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj).filter(k => obj[k] !== undefined).sort().map(k => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function sameStrokes(a: { objects?: unknown[] } | null | undefined, b: { objects?: unknown[] } | null | undefined): boolean {
  return canonicalJson(a?.objects ?? []) === canonicalJson(b?.objects ?? []);
}

interface PageCanvasState {
  fabricCanvas: fabric.Canvas | null;
  markup: Markup | null;
  undoStack: string[];
  redoStack: string[];
  isDirty: boolean;
  initialStateRecorded: boolean;
  width: number;
  height: number;
  loading: boolean;
  rendered: boolean;
  backgroundScale: number;
  /** W62: the strokes as last saved (the undo state); unset means as loaded (the first undo state) */
  savedState?: string;
}

/** Fine redlining: a zoom or pan settles this long before the page in view is drawn sharp */
export const SHARP_DEBOUNCE_MS = 250;

/** A rect, ellipse, cloud or line dragged less than this (screen px) is a stray click, not a shape */
export const MIN_SHAPE_PX = 4;

/** How far a shape was dragged, in canvas px: a line's length, otherwise the larger side of the dragged box */
export function dragExtent(tool: MarkupTool | null, x0: number, y0: number, x1: number, y1: number): number {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  return tool === MarkupTool.LINE ? Math.hypot(dx, dy) : Math.max(dx, dy);
}

/** 7.2: clearing a record's open as-built mark deletes it (the mark card's Delete copy) */
const DELETE_RECORD_AS_BUILT = {
  title: 'Delete as-built mark',
  message: 'Delete this as-built mark? It leaves the sheet and the review queue; its review history is kept.',
  confirm: 'Delete',
};

/** W62: how long Save reads "Saved ✓" after a save */
const SAVED_FLASH_MS = 2000;

@Component({
  selector: 'app-markup-editor',
  standalone: false,
  templateUrl: './markup-editor.component.html',
  styleUrls: ['./markup-editor.component.scss'],
})
export class MarkupEditorComponent implements AfterViewInit, OnDestroy {
  // Single-image canvas ref
  @ViewChild('markupCanvas', { static: false }) canvasRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('scrollContainer', { static: false }) scrollContainerRef: ElementRef<HTMLDivElement>;
  @ViewChild('conflictReload', { static: false }) conflictReloadRef: ElementRef<HTMLButtonElement>;
  @ViewChild('conflictKeep', { static: false }) conflictKeepRef: ElementRef<HTMLButtonElement>;

  @Input() config: MarkupEditorConfig;
  @Output() saved = new EventEmitter<MarkupSaveResult>();
  @Output() closed = new EventEmitter<void>();

  MarkupTool = MarkupTool;
  activeTool: MarkupTool | null = null;
  activeColor = '#2962FF';
  activeSize: StrokeSize = 'M';
  lineArrowMode = false;
  isSaving = false;
  saveError: string = null;
  isLoadingPage = false;

  readonly sizes: StrokeSize[] = ['S', 'M', 'L'];

  // Multi-page state
  pageCanvases: PageCanvasState[] = [];
  activePageIndex = 0;
  pageThumbnails: string[] = [];

  // Drawing sheet redline state (config.visibility set)
  redlineVisibility: 'shared' | 'private' = 'shared';
  /** W63: an as-built mark was just recorded (the note says so until the next stroke or switch) */
  asBuiltRecorded = false;
  private addedStrokesKey = '';
  private addedStrokesFrom = -1;
  /** Field note edits the page's layer; As-built records a new shared row on every save (spec §15) */
  redlineKind: 'note' | 'as_built' = 'note';
  /**
   * 7.2: the record's as-built mark loaded for editing that can no longer change (reviewed, or not the user's): its
   * strokes are on the canvas as a copy, and Save records them as a NEW open as-built mark for the record (P17)
   */
  asBuiltSource: Markup | null = null;
  /** 7.2: a record's edit of a locked as-built mark was just saved as a new mark (the note says so) */
  asBuiltCopied = false;
  /** Other visible rows on the page (tagged, carried, the other visibility), shown as images over the canvas */
  redlineLayers: Markup[] = [];
  /**
   * W76: the context row under the pointer (Select mode), with its box on the page, for the card that names it: "Sheet
   * redline · author · date", and "Edit sheet redline" when the caller may edit it
   */
  hoveredLayer: { row: Markup; box: MarkBox } | null = null;
  /** W76: switched from a record's row to the sheet's shared field note: saves go to that row, never to the host */
  sheetRedlineOnly = false;
  private contextBoxes = new WeakMap<Markup, MarkBox | null>();
  private hoverHideTimer: ReturnType<typeof setTimeout> | null = null;
  /** Another row under the pointer while a card is up: it takes over only if the pointer rests there (see trackContextHover) */
  private hoverSwitchTimer: ReturnType<typeof setTimeout> | null = null;
  /** The server's row after a version conflict; drives the Reload / Keep mine prompt */
  conflict: Markup | null = null;
  /** The row saved but its strokes-only preview did not upload: Retry sends only the preview */
  private failedPreview: { page: PageCanvasState; markup: Markup } | null = null;

  // Single-image state
  private singleCanvas: fabric.Canvas;
  private singleUndoStack: string[] = [];
  private singleRedoStack: string[] = [];
  private singleMarkup: Markup | null = null;
  private singleIsDirty = false;
  private singleInitialStateRecorded = false;
  private singleBackgroundScale = 1;
  private singleCanvasWidth = 0;
  private singleCanvasHeight = 0;

  readonly colorPalette = [
    '#2962FF', '#000000', '#1A237E', '#00BCD4',
    '#4CAF50', '#FFEB3B', '#FF9800', '#F44336',
  ];

  private readonly SIZE_MAP: Record<StrokeSize, { stroke: number; fontSize: number }> = {
    S: { stroke: 2, fontSize: 16 },
    M: { stroke: 4, fontSize: 24 },
    L: { stroke: 8, fontSize: 36 },
  };

  private isDrawingShape = false;
  private polygonPoints: { x: number; y: number }[] = [];
  private polygonLines: fabric.Line[] = [];
  private polygonDots: fabric.Circle[] = [];
  private shapeStartX = 0;
  private shapeStartY = 0;
  private shapeEndX = 0;
  private shapeEndY = 0;
  /** Fine redlining: the sheet editor's zoom and pan (fabric's viewportTransform); saved coordinates never see it */
  viewport: Viewport = FIT_VIEWPORT;
  /** Space held: a drag pans in any tool */
  spaceHeld = false;
  private panFrom: { x: number; y: number } | null = null;
  private readonly safariPinch = new SafariPinch();
  private unbindZoom: (() => void) | null = null;
  /** Fine redlining: the part of the page in view, re-rendered from the PDF at the zoom (page canvas units), under the strokes */
  private sharpPatch: { canvas: HTMLCanvasElement; x: number; y: number; w: number; h: number } | null = null;
  private sharpTimer: ReturnType<typeof setTimeout> | null = null;
  private sharpTask: { cancel(): void } | null = null;
  private sharpSeq = 0;
  private activeShape: fabric.Object;
  private skipSaveState = false;
  // objectUrls removed — no longer needed with PdfRenderService handling PDF data
  private intersectionObserver: IntersectionObserver;
  private pdfDoc: pdfjsLib.PDFDocumentProxy;
  private redlineRows: Markup[] = [];
  private conflictReturnFocus: HTMLElement | null = null;
  /** The as-built save in flight: a retry sends the same client_uuid so the API returns the row it made (R33) */
  private asBuiltClientUuid: string | null = null;

  constructor(
    private markupService: MarkupService,
    private pdfRenderService: PdfRenderService,
    private cdr: ChangeDetectorRef,
    private drawings: ProjectDrawingsService,
    private host: ElementRef<HTMLElement>,
  ) {}

  /** Review M1: the reason the open markup is locked */
  get lockedCopy(): string {
    const page = this.isSheetRedline ? this.pageCanvases[0] : this.config?.isDocument ? this.pageCanvases[this.activePageIndex] : null;
    return markupLockCopy(page ? page.markup : this.singleMarkup);
  }
  private readonly dialog = inject(MatDialog);
  /** W62: Save reads "Saved ✓" for a moment after a save */
  savedFlash = false;
  private savedFlashTimer: ReturnType<typeof setTimeout> | null = null;
  /** W62: the photo's strokes as last saved (unset: as loaded) */
  private singleSavedState: string | undefined;

  /**
   * W62: the strokes differ from the last save (or from what was loaded): the undo state now against the saved one,
   * so undoing back to it is clean again and redo dirty. Changing Shared/Private or Field note/As-built loads another
   * row and is not a change by itself (Save sends only the strokes).
   */
  get hasChanges(): boolean {
    if (this.isSheetRedline) {
      return !!this.pageCanvases[0] && this.pageChanged(this.pageCanvases[0]);
    }
    if (this.config?.isDocument) {
      return this.pageCanvases.some(p => this.pageChanged(p));
    }
    const top = this.singleUndoStack[this.singleUndoStack.length - 1];
    return !!top && top !== (this.singleSavedState ?? this.singleUndoStack[0]);
  }

  /** Save is offered only when there is something new to save */
  get canSave(): boolean {
    return this.hasChanges && !this.isSaving && !this.rowLocked && !this.conflict;
  }

  private pageChanged(page: PageCanvasState): boolean {
    const top = page.undoStack[page.undoStack.length - 1];
    return !!top && top !== (page.savedState ?? page.undoStack[0]);
  }

  /** A save landed: what is on the canvas now is the saved state, and Save says so for a moment */
  private markSaved(page?: PageCanvasState | null): void {
    if (page) {
      page.savedState = page.undoStack[page.undoStack.length - 1];
    } else {
      this.singleSavedState = this.singleUndoStack[this.singleUndoStack.length - 1];
    }
    this.savedFlash = true;
    if (this.savedFlashTimer) clearTimeout(this.savedFlashTimer);
    this.savedFlashTimer = setTimeout(() => {
      this.savedFlash = false;
      this.savedFlashTimer = null;
      this.cdr.markForCheck();
    }, SAVED_FLASH_MS);
  }

  /**
   * W59: the markup open here belongs to someone else (shared markup is its author's and the project admins'). It
   * stays visible, but the tools, Delete and Save are off; on a sheet, Private (the user's own row) is always theirs.
   */
  get rowLocked(): boolean {
    if (this.config?.readOnly) return false;
    if (this.isSheetRedline) return this.pageCanvases[0]?.markup?.can_edit === false;
    // A photo's markup, or the open page's of a document
    const markup = this.config?.isDocument ? this.pageCanvases[this.activePageIndex]?.markup : this.singleMarkup;
    return markup?.can_edit === false;
  }

  get isSheetRedline(): boolean {
    return !!this.config?.visibility;
  }

  /** A record's redline (its design-context link, saved or not yet): one shared row, so no Shared/Private toggle */
  get isLinkRedline(): boolean {
    return this.isSheetRedline && (!!this.config.linkUuid || !!this.config.deferSave);
  }

  get canRetryPreview(): boolean {
    return !!this.failedPreview;
  }

  /** Field note or As-built: the page's own redline and (7.2) a record's markup; not an as-built mark's own edit */
  get showKindChoice(): boolean {
    return this.isSheetRedline && !this.config.asBuiltMarkup;
  }

  get isAsBuiltMode(): boolean {
    return this.isSheetRedline && this.redlineKind === 'as_built';
  }

  /** As-built, recording new marks (not editing one): each save is a new row and the canvas clears after it */
  get recordsAsBuilt(): boolean {
    return this.isAsBuiltMode && !this.config.asBuiltMarkup && !this.isLinkRedline;
  }

  /** As-built marks are always shared, so the Shared/Private toggle goes */
  get showVisibilityToggle(): boolean {
    return this.isSheetRedline && !this.isLinkRedline && !this.isAsBuiltMode;
  }

  /**
   * The row this editor edits on the page: a record's held strokes, the row tagged with its link, or (page redline)
   * the untagged row of the chosen visibility
   */
  editedRow(rows: Markup[]): Markup | null {
    if (this.config?.asBuiltMarkup) {
      return (rows || []).find(r => r.id === this.config.asBuiltMarkup.id) || this.config.asBuiltMarkup;
    }
    this.asBuiltSource = null;
    if (this.config?.deferSave && !this.config.linkUuid) {
      const held = this.config.existingMarkup || null;
      return held && (held.kind === 'as_built') === this.isAsBuiltMode ? held : null;
    }
    if (this.config?.linkUuid) {
      if (this.isAsBuiltMode) return this.recordAsBuiltRow(recordAsBuiltRows(rows, this.config.linkUuid)[0] ?? null);
      return (rows || []).find(r => r.link_uuid === this.config.linkUuid && r.visibility === 'shared' && r.kind !== 'as_built') || null;
    }
    // Recording as-built: every mark starts on an empty canvas
    if (this.isAsBuiltMode) return null;
    return MarkupService.pickPageRedline(rows, this.redlineVisibility);
  }

  /**
   * 7.2: the record's newest as-built mark. Its author edits it while it is open; otherwise (reviewed, or someone
   * else's) its strokes load as a copy that saves as a new open mark (P17), and the mark itself stays as it is.
   */
  private recordAsBuiltRow(row: Markup | null): Markup | null {
    if (!row || (row.review_status ?? 'open') === 'open' && row.can_edit !== false) return row;
    this.asBuiltSource = row;
    return new Markup({
      annotation_data: row.annotation_data, canvas_width: row.canvas_width, canvas_height: row.canvas_height,
      attachment_id: row.attachment_id, page_number: row.page_number, visibility: 'shared', kind: 'as_built', link_uuid: row.link_uuid,
    });
  }

  /**
   * 7.2: the kind a record's markup opens on: what it already is on this page (held strokes, or its tagged rows: a note
   * wins over as-built, as one record's markup on a sheet is one kind), else the host's choice, else Field note
   */
  private initialRecordKind(rows: Markup[]): 'note' | 'as_built' {
    const fallback = this.config.recordKind === 'as_built' ? 'as_built' : 'note';
    if (this.config.deferSave && !this.config.linkUuid) {
      const held = this.config.existingMarkup;
      return held ? (held.kind === 'as_built' ? 'as_built' : 'note') : fallback;
    }
    const tagged = (rows || []).filter(r => r.link_uuid === this.config.linkUuid && r.visibility === 'shared');
    if (tagged.some(r => r.kind !== 'as_built' && r.annotation_data?.objects?.length)) return 'note';
    return tagged.some(r => r.kind === 'as_built') ? 'as_built' : fallback;
  }

  /** Unsaved strokes on the sheet page; the Shared/Private toggle waits until they are saved or undone */
  get hasUnsavedRedline(): boolean {
    const page = this.pageCanvases[0];
    return !!page && this.pageChanged(page);
  }

  get showSidebar(): boolean {
    return this.config?.isDocument && this.pageCanvases.length > 1;
  }

  get canUndo(): boolean {
    if (this.config?.isDocument) {
      const page = this.pageCanvases[this.activePageIndex];
      return page?.undoStack.length > 1;
    }
    return this.singleUndoStack.length > 1;
  }

  get canRedo(): boolean {
    if (this.config?.isDocument) {
      const page = this.pageCanvases[this.activePageIndex];
      return page?.redoStack.length > 0;
    }
    return this.singleRedoStack.length > 0;
  }

  ngAfterViewInit() {
    setTimeout(() => this.init(), 50);
  }

  ngOnDestroy() {
    if (this.savedFlashTimer) clearTimeout(this.savedFlashTimer);
    this.unbindZoom?.();
    this.cancelSharp();
    this.keepContextCard();
    // Dispose all fabric canvases
    if (this.config?.isDocument) {
      this.pageCanvases.forEach(p => p.fabricCanvas?.dispose());
    } else if (this.singleCanvas) {
      this.singleCanvas.dispose();
    }
    this.intersectionObserver?.disconnect();
    // No object URLs to clean up — PdfRenderService manages PDF data
    this.pdfRenderService.destroy();
  }

  @HostListener('document:keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (event.key !== ' ' || !this.spaceHeld) return;
    this.spaceHeld = false;
    const canvas = this.getActiveCanvas();
    if (canvas) {
      canvas.skipTargetFind = false;
      this.applyToolToCanvas(canvas);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // The conflict prompt is modal: nothing may change the strokes it is about to resolve.
    if (this.conflict) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

    const canvas = this.getActiveCanvas();
    const activeObj = canvas?.getActiveObject();
    if (activeObj?.type === 'i-text' && (activeObj as fabric.IText).isEditing) return;

    if (event.key === ' ' && this.isSheetRedline) {
      // Space + drag pans the zoomed sheet in any tool (and never scrolls the page under the editor)
      event.preventDefault();
      if (!this.spaceHeld) {
        this.spaceHeld = true;
        if (canvas) {
          canvas.isDrawingMode = false;
          canvas.selection = false;
          canvas.skipTargetFind = true;
          canvas.defaultCursor = 'grab';
        }
      }
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
      event.preventDefault();
      this.undo();
    } else if ((event.ctrlKey || event.metaKey) && event.key === 'y') {
      event.preventDefault();
      this.redo();
    } else if (event.key === 'Escape') {
      // Stop propagation so Escape doesn't bubble up and close the parent dialog/overlay
      event.preventDefault();
      event.stopPropagation();
      this.activeTool = null;
      this.applyToolToCanvas(canvas);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.canDelete) {
        event.preventDefault();
        this.deleteSelected();
      }
    }
  }

  /**
   * W76: the Select tool (the arrow): no drawing tool, so shapes can be picked, marqueed and moved; the state Esc gives
   */
  selectMode(): void {
    if (this.rowLocked) return;
    this.activeTool = null;
    const canvas = this.getActiveCanvas();
    if (canvas) this.applyToolToCanvas(canvas);
  }

  /** W76: something editable is selected (one shape or a marquee of several) and may be deleted */
  get canDelete(): boolean {
    if (this.activeTool !== null || this.rowLocked || this.conflict) return false;
    const canvas = this.getActiveCanvas();
    const active = canvas?.getActiveObject();
    if (!active || (active.type === 'i-text' && (active as fabric.IText).isEditing)) return false;
    return canvas.getActiveObjects().some(o => o.selectable !== false);
  }

  /** W76: the toolbar's Delete and the Delete / Backspace keys: removes every selected shape (as mobile's deleteSelected) */
  deleteSelected(): void {
    if (!this.canDelete) return;
    const canvas = this.getActiveCanvas();
    const objects = canvas.getActiveObjects();
    canvas.discardActiveObject();
    objects.forEach(o => canvas.remove(o));
    canvas.requestRenderAll();
    this.saveStateForCanvas(canvas);
  }

  // --- Tool management ---

  selectTool(tool: MarkupTool) {
    if (this.rowLocked) return;
    // Toggle off if already active
    if (this.activeTool === tool) {
      this.activeTool = null;
    } else {
      this.activeTool = tool;
    }
    const canvas = this.getActiveCanvas();
    if (canvas) this.applyToolToCanvas(canvas);
  }

  setSize(size: StrokeSize) {
    this.activeSize = size;
    const canvas = this.getActiveCanvas();
    if (canvas?.isDrawingMode) {
      canvas.freeDrawingBrush.width = this.strokeWidth;
    }
  }

  toggleLineArrow() {
    this.lineArrowMode = !this.lineArrowMode;
  }

  /** Fine redlining: the chosen size / zoom (a floor keeps it visible), so it looks the same and lands finer when zoomed */
  get strokeWidth(): number {
    return zoomedSize(this.SIZE_MAP[this.activeSize].stroke, this.zoom);
  }

  get fontSize(): number {
    return zoomedSize(this.SIZE_MAP[this.activeSize].fontSize, this.zoom, FONT_FLOOR_PX);
  }

  setColor(color: string) {
    this.activeColor = color;
    const canvas = this.getActiveCanvas();
    if (canvas?.isDrawingMode) {
      canvas.freeDrawingBrush.color = color;
    }
  }

  undo() {
    if (this.conflict) return;
    if (this.config?.isDocument) {
      const page = this.pageCanvases[this.activePageIndex];
      if (!page || page.undoStack.length <= 1) return;
      const current = page.undoStack.pop();
      page.redoStack.push(current);
      this.restoreStateForPage(page, page.undoStack[page.undoStack.length - 1]);
    } else {
      if (this.singleUndoStack.length <= 1) return;
      const current = this.singleUndoStack.pop();
      this.singleRedoStack.push(current);
      this.restoreCanvasState(this.singleCanvas, this.singleUndoStack[this.singleUndoStack.length - 1]);
    }
  }

  redo() {
    if (this.conflict) return;
    if (this.config?.isDocument) {
      const page = this.pageCanvases[this.activePageIndex];
      if (!page || page.redoStack.length === 0) return;
      const next = page.redoStack.pop();
      page.undoStack.push(next);
      this.restoreStateForPage(page, next);
    } else {
      if (this.singleRedoStack.length === 0) return;
      const next = this.singleRedoStack.pop();
      this.singleUndoStack.push(next);
      this.restoreCanvasState(this.singleCanvas, next);
    }
  }

  onFilenameChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.config) this.config.filename = input.value;
  }

  /** W62: unsaved strokes ask first (the app's "Unsaved changes" confirm) */
  close() {
    if (this.conflict) return;
    if (!this.hasChanges || this.isSaving) {
      this.closed.emit();
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Unsaved changes', 'There are unsaved changes.<br/>Are you sure you want to discard?'),
    }).afterClosed().subscribe(discard => {
      if (discard) this.closed.emit();
    });
  }

  // --- Page navigation ---

  scrollToPage(index: number) {
    this.setActivePage(index);
    const wrapper = this.scrollContainerRef?.nativeElement?.querySelector(`[data-page="${index}"]`);
    wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  setActivePage(index: number) {
    if (this.activePageIndex === index) return;
    this.activePageIndex = index;
    const canvas = this.pageCanvases[index]?.fabricCanvas;
    if (canvas) this.applyToolToCanvas(canvas);
  }

  // --- Save ---

  saveAll() {
    if (this.isSaving || this.conflict || this.rowLocked || !this.hasChanges) return;
    const empty = this.isSheetRedline && !this.pageCanvases[0]?.fabricCanvas?.getObjects().length;
    if (empty && this.config?.emptySaveError) {
      this.saveError = this.config.emptySaveError;
      return;
    }
    // 7.2: emptying the record's own open as-built mark deletes it, so it asks as the mark card's Delete does
    const deletesAsBuilt = empty && this.isLinkRedline && this.isAsBuiltMode && !!this.pageCanvases[0]?.markup?.id;
    const ask = this.isSheetRedline ? this.config?.confirmEmptySave ?? (deletesAsBuilt ? DELETE_RECORD_AS_BUILT : null) : null;
    if (ask && empty) {
      this.dialog.open(ConfirmDialogComponent, { disableClose: true, data: new ConfirmDialogModel(ask.title, ask.message, ask.confirm, 'Cancel', true) })
        .afterClosed().subscribe(go => { if (go) this.startSave(); });
      return;
    }
    this.startSave();
  }

  private startSave() {
    if (this.isSaving) return;
    this.isSaving = true;
    this.saveError = null;
    this.failedPreview = null;

    if (this.isSheetRedline) {
      this.saveSheetRedline();
    } else if (this.config?.isDocument) {
      this.saveAllPages();
    } else {
      this.saveSingleImage();
    }
  }

  /** Switches which row is edited (the page's shared row or the user's private one); never a row's visibility */
  switchVisibility(visibility: 'shared' | 'private') {
    const page = this.pageCanvases[0];
    if (visibility === this.redlineVisibility || this.isSaving || this.hasUnsavedRedline || !page?.fabricCanvas) return;
    this.redlineVisibility = visibility;
    this.loadRowIntoPage(page, MarkupService.pickPageRedline(this.redlineRows, visibility));
    this.refreshRedlineLayers();
  }

  /** Field note or As-built; like Shared/Private it waits until unsaved strokes are saved or undone */
  /**
   * W63: Field note ↔ As-built. Strokes drawn since the last save go with the mode (drawn as a field note, then
   * switched, they become the as-built mark, and back), one undo step each. Only blocked when the unsaved change also
   * edits the row's existing strokes, which belong to that row.
   */
  switchKind(kind: 'note' | 'as_built') {
    const page = this.pageCanvases[0];
    if (!this.showKindChoice || kind === this.redlineKind || this.isSaving || !page?.fabricCanvas) return;
    // 7.2: a record's markup is one kind: it switches only with nothing on the canvas
    if (this.isLinkRedline && !this.canSwitchKind) return;
    const carried = this.hasUnsavedRedline && !this.isLinkRedline ? this.addedStrokes(page) : [];
    if (carried === null) return;
    this.redlineKind = kind;
    this.asBuiltClientUuid = null;
    this.asBuiltRecorded = false;
    this.asBuiltCopied = false;
    this.saveError = null;
    this.failedPreview = null;
    if (kind === 'as_built') this.redlineVisibility = 'shared';
    this.loadRowIntoPage(page, this.editedRow(this.redlineRows), () => this.carryStrokes(page, carried));
    this.refreshRedlineLayers();
  }

  /** Field note / As-built can change now: nothing unsaved, or only new strokes (which go along) */
  get canSwitchKind(): boolean {
    const page = this.pageCanvases[0];
    // 7.2: a record's markup on a sheet is one kind, chosen before the first stroke
    if (this.isLinkRedline) return !page?.fabricCanvas?.getObjects().length;
    return !this.hasUnsavedRedline || (!!page && this.addedStrokes(page) !== null);
  }

  /** Why Field note / As-built is off (the white tooltip) */
  get kindLockCopy(): string {
    return this.isLinkRedline ? 'A record\'s markup on a sheet is one type. Clear its strokes to change it.' : 'Save or undo your changes first';
  }

  /**
   * The strokes added since the row loaded (or the last save), when every stroke that was there is unchanged; null
   * when the unsaved change also edits or removes those. Read from the undo states, so undo / redo (which rebuild
   * the objects) never confuse it; new strokes are always appended.
   */
  private addedStrokes(page: PageCanvasState): fabric.Object[] | null {
    const canvas = page.fabricCanvas;
    // Review I2: since the LAST SAVE (the W62 snapshot), not since the row loaded: saved strokes stay on their row
    const baseState = page.savedState ?? page.undoStack[0];
    const topState = page.undoStack[page.undoStack.length - 1];
    if (!canvas || !baseState || !topState) return null;
    const key = baseState + '\u0000' + topState;
    if (this.addedStrokesKey !== key) {
      const base: unknown[] = JSON.parse(baseState).objects || [];
      const now: unknown[] = JSON.parse(topState).objects || [];
      const kept = now.length >= base.length && base.every((o, i) => JSON.stringify(o) === JSON.stringify(now[i]));
      this.addedStrokesKey = key;
      this.addedStrokesFrom = kept ? base.length : -1;
    }
    return this.addedStrokesFrom < 0 ? null : canvas.getObjects().slice(this.addedStrokesFrom);
  }

  /** Puts the carried strokes on the newly loaded row, one undo step each, as unsaved */
  private carryStrokes(page: PageCanvasState, strokes: fabric.Object[]) {
    const canvas = page.fabricCanvas;
    if (!canvas || !strokes.length) return;
    for (const stroke of strokes) {
      canvas.add(stroke);
      page.undoStack.push(this.getCanvasState(canvas));
    }
    page.redoStack = [];
    page.isDirty = true;
    this.applyToolToCanvas(canvas);
    canvas.renderAll();
  }

  /** W63: As-built with nothing drawn since the last mark: Save has nothing to record */
  get nothingToRecord(): boolean {
    // Every as-built mark starts on an empty canvas, so "nothing new since the last save" is "nothing drawn"
    return this.recordsAsBuilt && !this.hasUnsavedRedline;
  }

  /** Reload: show the server's row and drop local strokes. Keep mine: re-send local strokes over its version. */
  resolveConflict(choice: 'reload' | 'keep') {
    const current = this.conflict;
    const page = this.pageCanvases[0];
    if (!current || !page) return;
    this.conflict = null;
    this.redlineRows = [...this.redlineRows.filter(r => r.id !== current.id), current];
    if (choice === 'reload') {
      this.loadRowIntoPage(page, current);
    } else {
      page.markup = current;
      this.saveAll();
    }
    this.cdr.detectChanges();
    this.restoreFocusAfterConflict();
  }

  /** Keeps Tab and Shift+Tab on the prompt's two buttons */
  onConflictKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') return;
    const reload = this.conflictReloadRef?.nativeElement;
    const keep = this.conflictKeepRef?.nativeElement;
    if (!reload || !keep) return;
    event.preventDefault();
    const next = document.activeElement === reload ? keep : reload;
    next.focus();
  }

  // =====================================================================
  // INITIALIZATION
  // =====================================================================

  private init() {
    if (this.isSheetRedline) {
      this.initSheetPage();
    } else if (this.config?.isDocument) {
      this.initMultiPage();
    } else {
      this.initSingleImage();
    }
  }

  // --- Single image initialization ---

  private initSingleImage() {
    this.singleMarkup = this.config.existingMarkup || null;
    this.singleCanvas = new fabric.Canvas(this.canvasRef.nativeElement, {
      selection: true,
      preserveObjectStacking: true,
    });
    this.bindCanvasEvents(this.singleCanvas, null);
    this.pageThumbnails = [this.config.imageUrl];

    this.loadCorsImage(this.config.imageUrl, (img) => {
      const { width, height, scale } = this.calculateCanvasSize(img.width, img.height);
      this.singleBackgroundScale = scale;
      this.singleCanvasWidth = width;
      this.singleCanvasHeight = height;

      this.singleCanvas.setWidth(width);
      this.singleCanvas.setHeight(height);
      img.scaleX = scale;
      img.scaleY = scale;
      this.singleCanvas.setBackgroundImage(img, () => {
        this.singleCanvas.renderAll();
        this.loadExistingMarkupForCanvas(this.singleCanvas, this.singleMarkup, () => {
          this.saveSingleState();
          const thumb = this.singleCanvas.toDataURL({ format: 'jpeg', quality: 0.85 });
          this.pageThumbnails = [thumb];
        });
      });
    });

    this.applyToolToCanvas(this.singleCanvas);
  }

  // --- Multi-page PDF initialization ---

  private async initMultiPage() {
    const pdfUrl = this.config.originalUrl || this.config.imageUrl;

    try {
      this.pdfDoc = await this.pdfRenderService.loadDocument(pdfUrl);
      const numPages = this.pdfDoc.numPages;

      // Initialize page state array
      this.pageCanvases = [];
      this.pageThumbnails = new Array(numPages).fill(null);

      // Get dimensions for all pages to set up placeholders
      const containerWidth = this.getCanvasContainerWidth();
      for (let i = 0; i < numPages; i++) {
        const pageInfo = await this.pdfRenderService.getPageInfo(this.pdfDoc, i + 1);
        const scale = containerWidth / pageInfo.width;
        const scaledWidth = Math.floor(pageInfo.width * scale);
        const scaledHeight = Math.floor(pageInfo.height * scale);

        this.pageCanvases.push({
          fabricCanvas: null,
          markup: null,
          undoStack: [],
          redoStack: [],
          isDirty: false,
          initialStateRecorded: false,
          width: scaledWidth,
          height: scaledHeight,
          loading: false,
          rendered: false,
          backgroundScale: scale,
        });
      }

      // Load existing markups for all pages
      const allMarkups = await this.markupService.getByAttachment(this.config.attachmentId).toPromise();
      if (allMarkups?.length) {
        allMarkups.forEach(m => {
          if (m.page_number >= 1 && m.page_number <= numPages) {
            this.pageCanvases[m.page_number - 1].markup = m;
            if (m.preview_url) {
              this.pageThumbnails[m.page_number - 1] = m.preview_url;
            }
          }
        });
      }

      this.cdr.detectChanges();

      // Generate thumbnails for all pages (small scale for speed)
      this.generateAllThumbnails(numPages);

      // Set up IntersectionObserver for lazy rendering
      setTimeout(() => this.setupIntersectionObserver(), 100);

    } catch (err) {
      console.error('Failed to load PDF:', err);
      // Fallback: treat as single image
      this.config.isDocument = false;
      this.initSingleImage();
    }
  }

  // --- Drawing sheet page initialization: one page of the set, never the whole document ---

  private async initSheetPage() {
    this.redlineVisibility = this.config.visibility;
    if (this.config.asBuiltMarkup) {
      this.redlineKind = 'as_built';
      this.redlineVisibility = 'shared';
    }
    try {
      // A set can be 150 sheets: fetch only the byte ranges this page needs (with S3 exposing the range headers).
      this.pdfDoc = await this.pdfRenderService.loadDocument(this.config.originalUrl, { disableStream: true, disableAutoFetch: true });
      const pageInfo = await this.pdfRenderService.getPageInfo(this.pdfDoc, this.config.pageNumber);
      const scale = this.getCanvasContainerWidth() / pageInfo.width;
      this.pageCanvases = [{
        fabricCanvas: null,
        markup: null,
        undoStack: [],
        redoStack: [],
        isDirty: false,
        initialStateRecorded: false,
        width: Math.floor(pageInfo.width * scale),
        height: Math.floor(pageInfo.height * scale),
        loading: false,
        rendered: false,
        backgroundScale: scale,
      }];
      this.pageThumbnails = [null];

      this.redlineRows = await this.markupService.getByAttachment(this.config.attachmentId, this.config.pageNumber).toPromise();
      if (this.isLinkRedline && !this.config.asBuiltMarkup) {
        this.redlineKind = this.initialRecordKind(this.redlineRows);
        if (this.redlineKind === 'as_built') this.redlineVisibility = 'shared';
      }
      this.pageCanvases[0].markup = this.editedRow(this.redlineRows);
      this.refreshRedlineLayers();
      this.cdr.detectChanges();
      this.renderPdfPage(0);
    } catch (err) {
      console.error('Failed to load sheet page:', err);
      this.saveError = 'This sheet could not be opened for redlining. Try again.';
      this.cdr.detectChanges();
    }
  }

  private async generateAllThumbnails(numPages: number) {
    // Render small thumbnails for sidebar — don't block, run in background
    for (let i = 0; i < numPages; i++) {
      if (this.pageThumbnails[i]) continue; // Already has markup preview
      try {
        const dataUrl = await this.pdfRenderService.renderPageAsDataUrl(this.pdfDoc, i + 1, 0.3);
        this.pageThumbnails[i] = dataUrl;
        this.cdr.detectChanges();
      } catch {
        // Silent — thumbnail stays as placeholder
      }
    }
  }

  private setupIntersectionObserver() {
    const container = this.scrollContainerRef?.nativeElement;
    if (!container) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        // Trigger lazy rendering for newly visible pages + auto-save pages scrolling away
        entries.forEach(entry => {
          const pageIndex = parseInt(entry.target.getAttribute('data-page'), 10);
          if (isNaN(pageIndex)) return;

          if (entry.isIntersecting && !this.pageCanvases[pageIndex].rendered) {
            this.renderPdfPage(pageIndex);
          }

          // Auto-save dirty pages when they scroll out of view
          if (!entry.isIntersecting) {
            const page = this.pageCanvases[pageIndex];
            if (page?.isDirty && page.fabricCanvas) {
              this.autoSavePage(page, pageIndex);
            }
          }
        });

        // Pick the most-visible page as active
        const bestEntry = entries
          .filter(e => e.isIntersecting)
          .reduce((best, e) => (!best || e.intersectionRatio > best.intersectionRatio) ? e : best, null as IntersectionObserverEntry | null);
        if (bestEntry) {
          const idx = parseInt(bestEntry.target.getAttribute('data-page'), 10);
          if (!isNaN(idx) && idx !== this.activePageIndex) {
            this.activePageIndex = idx;
            this.cdr.detectChanges();
          }
        }
      },
      {
        root: container,
        rootMargin: '200px 0px', // Pre-render pages 200px before they enter viewport
        threshold: [0, 0.3, 0.5, 1.0],
      }
    );

    // Observe all page wrappers
    container.querySelectorAll('.page-canvas-wrapper').forEach(el => {
      this.intersectionObserver.observe(el);
    });
  }

  private async renderPdfPage(pageIndex: number) {
    const pageState = this.pageCanvases[pageIndex];
    if (pageState.rendered || pageState.loading) return;

    pageState.loading = true;
    this.cdr.detectChanges();

    try {
      const pageNum = this.isSheetRedline ? this.config.pageNumber : pageIndex + 1;
      const containerWidth = this.getCanvasContainerWidth();

      // Get page info for proper scaling
      const pageInfo = await this.pdfRenderService.getPageInfo(this.pdfDoc, pageNum);
      const scale = containerWidth / pageInfo.width;
      const scaledWidth = Math.floor(pageInfo.width * scale);
      const scaledHeight = Math.floor(pageInfo.height * scale);

      pageState.width = scaledWidth;
      pageState.height = scaledHeight;
      pageState.backgroundScale = scale;

      // Render the page to get background image
      const dataUrl = await this.pdfRenderService.renderPageAsDataUrl(
        this.pdfDoc, pageNum, this.isSheetRedline ? this.sheetRenderScale(pageInfo) : undefined
      );

      // Update thumbnail if no markup preview exists
      if (!this.pageThumbnails[pageIndex]) {
        this.pageThumbnails[pageIndex] = dataUrl;
      }

      // Initialize fabric canvas for this page
      const canvasEl = document.getElementById(`page-canvas-${pageIndex}`) as HTMLCanvasElement;
      if (!canvasEl) {
        pageState.loading = false;
        return;
      }

      const fabricCanvas = new fabric.Canvas(canvasEl, {
        selection: true,
        preserveObjectStacking: true,
      });

      fabricCanvas.setWidth(scaledWidth);
      fabricCanvas.setHeight(scaledHeight);

      // Set background
      fabric.Image.fromURL(dataUrl, (img) => {
        if (!img?.width || !img?.height) {
          pageState.loading = false;
          return;
        }
        const bgScale = scaledWidth / img.width;
        img.scaleX = bgScale;
        img.scaleY = bgScale;
        fabricCanvas.setBackgroundImage(img, () => {
          fabricCanvas.renderAll();

          // Load existing markup annotations
          this.loadExistingMarkupForCanvas(fabricCanvas, pageState.markup, () => {
            // Save initial state
            const state = this.getCanvasState(fabricCanvas);
            pageState.undoStack.push(state);
            pageState.initialStateRecorded = true;

            // Update thumbnail with markups
            const thumb = fabricCanvas.toDataURL({ format: 'jpeg', quality: 0.85 });
            this.pageThumbnails[pageIndex] = thumb;

            pageState.loading = false;
            pageState.rendered = true;
            pageState.fabricCanvas = fabricCanvas;

            this.bindCanvasEvents(fabricCanvas, pageIndex);
            if (this.isSheetRedline) this.bindZoom(fabricCanvas);
            this.applyToolToCanvas(fabricCanvas);
            this.cdr.detectChanges();
          }, scaledWidth);
        });
      }, { crossOrigin: 'anonymous' });

    } catch (err) {
      console.error(`Failed to render page ${pageIndex + 1}:`, err);
      pageState.loading = false;
      this.cdr.detectChanges();
    }
  }

  // =====================================================================
  // ZOOM AND PAN (fine redlining, sheet mode)
  // =====================================================================

  get zoom(): number {
    return this.viewport[0];
  }

  get canZoomIn(): boolean {
    return this.zoom < MARKUP_ZOOM_MAX - 1e-6;
  }

  get canZoomOut(): boolean {
    return this.zoom > 1 + 1e-6;
  }

  /** The zoom's CSS for the page-sized HTML layers (faded rows, their hotspots), so they stay on the sheet */
  get overlayTransform(): string {
    return viewportCss(this.viewport);
  }

  /** A page point (0-1 of the page) as % of the canvas after the zoom: the pin and the context card, which keep their size */
  overlayAt(x: number, y: number): { left: number; top: number } {
    const page = this.pageCanvases[0];
    if (!page?.width || !page.height) return { left: x * 100, top: y * 100 };
    return viewportPercent(this.viewport, x * page.width, y * page.height, page.width, page.height);
  }

  zoomIn(): void {
    this.zoomToStep(1);
  }

  zoomOut(): void {
    this.zoomToStep(-1);
  }

  /** Back to the whole page width */
  fitView(): void {
    this.setViewport(FIT_VIEWPORT);
  }

  /** + / −: the next step, about the middle of the canvas */
  private zoomToStep(step: 1 | -1): void {
    const page = this.pageCanvases[0];
    if (!page?.fabricCanvas) return;
    this.setViewport(zoomViewportAt(this.viewport, stepZoom(MARKUP_ZOOMS, this.zoom, step), page.width / 2, page.height / 2, page.width, page.height));
  }

  /** Applies a view (clamped to the page) to the canvas; objects keep their page coordinates */
  setViewport(vpt: Viewport): void {
    const page = this.pageCanvases[0];
    const canvas = page?.fabricCanvas;
    if (!canvas) return;
    const wasZoomed = this.zoom > 1;
    this.viewport = clampViewport(vpt, page.width, page.height);
    canvas.setViewportTransform([...this.viewport]);
    // The next freehand stroke follows the zoom; strokes already drawn keep their width
    if (canvas.freeDrawingBrush) canvas.freeDrawingBrush.width = this.strokeWidth;
    this.scheduleSharp();
    if (wasZoomed !== this.zoom > 1 && !this.spaceHeld) this.applyToolToCanvas(canvas);
    this.cdr.detectChanges();
  }

  /** ctrl / ⌘ + wheel or a trackpad pinch zooms at the pointer; a plain wheel pans the zoomed sheet (at fit it scrolls) */
  private onZoomWheel(canvas: fabric.Canvas, event: WheelEvent): void {
    const page = this.pageCanvases[0];
    if (!page) return;
    const box = canvas.getElement().getBoundingClientRect();
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      if (this.safariPinch.active) return;
      this.setViewport(zoomViewportAt(this.viewport, this.zoom * wheelZoomFactor(event.deltaY), event.clientX - box.left, event.clientY - box.top,
        page.width, page.height));
    } else if (this.zoom > 1) {
      event.preventDefault();
      this.setViewport(panViewport(this.viewport, -event.deltaX, -event.deltaY, page.width, page.height));
    }
  }

  private bindZoom(canvas: fabric.Canvas): void {
    this.unbindZoom?.();
    this.drawSharpUnderStrokes(canvas);
    const el = (canvas as unknown as { wrapperEl?: HTMLElement }).wrapperEl ?? canvas.getElement().parentElement;
    if (!el) return;
    this.unbindZoom = bindSheetZoomListeners(el, {
      wheel: e => this.onZoomWheel(canvas, e),
      gesturestart: e => this.safariPinch.start(e, this.zoom),
      gesturechange: e => {
        const next = this.safariPinch.change(e);
        const page = this.pageCanvases[0];
        if (next === null || !page) return;
        const box = canvas.getElement().getBoundingClientRect();
        const g = e as unknown as { clientX: number; clientY: number };
        this.setViewport(zoomViewportAt(this.viewport, next, g.clientX - box.left, g.clientY - box.top, page.width, page.height));
      },
      gestureend: e => this.safariPinch.end(e),
    });
  }

  /**
   * The sharp patch goes between the page image (fabric's background) and the strokes: drawn right after the background,
   * through the zoom. Never in a strokes-only export (it nulls the background) and never saved: it is not an object.
   */
  private drawSharpUnderStrokes(canvas: fabric.Canvas): void {
    const host = canvas as unknown as { _renderBackground(ctx: CanvasRenderingContext2D): void };
    const base = host._renderBackground.bind(canvas);
    host._renderBackground = (ctx: CanvasRenderingContext2D) => {
      base(ctx);
      const patch = this.sharpPatch;
      if (!patch || !canvas.backgroundImage) return;
      const v = canvas.viewportTransform;
      ctx.save();
      ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
      ctx.drawImage(patch.canvas, patch.x, patch.y, patch.w, patch.h);
      ctx.restore();
    };
  }

  /** After a zoom or pan settles, the page in view is re-rendered sharp; a newer gesture cancels the one under way */
  private scheduleSharp(): void {
    this.cancelSharp();
    if (this.zoom <= 1) {
      if (this.sharpPatch) {
        this.sharpPatch = null;
        this.pageCanvases[0]?.fabricCanvas?.requestRenderAll();
      }
      return;
    }
    this.sharpTimer = setTimeout(() => {
      this.sharpTimer = null;
      void this.renderSharp();
    }, SHARP_DEBOUNCE_MS);
  }

  private cancelSharp(): void {
    if (this.sharpTimer) clearTimeout(this.sharpTimer);
    this.sharpTimer = null;
    this.sharpTask?.cancel();
    this.sharpTask = null;
    this.sharpSeq++;
  }

  /**
   * The visible part of the zoomed page from the sheet's PDF page at the screen's resolution (sharpRegion keeps it inside
   * the browser's canvas limits). The old patch stays until the new one is ready: it is in page units, so still in place.
   */
  private async renderSharp(): Promise<void> {
    const page = this.pageCanvases[0];
    const canvas = page?.fabricCanvas;
    if (!canvas || !this.pdfDoc || this.zoom <= 1) return;
    const seq = ++this.sharpSeq;
    const v = this.viewport;
    const box = canvas.getElement().getBoundingClientRect();
    const scroller = this.scrollContainerRef?.nativeElement?.getBoundingClientRect();
    const pane = scroller?.width && scroller.height ? scroller : box;
    const left = Math.max(box.left, pane.left);
    const top = Math.max(box.top, pane.top);
    const view = { left, top, width: Math.min(box.right, pane.right) - left, height: Math.min(box.bottom, pane.bottom) - top };
    const pageBox = { left: box.left + v[4], top: box.top + v[5], width: page.width * v[0], height: page.height * v[0] };
    const region = view.width > 0 && view.height > 0 ? sharpRegion(pageBox, view, window.devicePixelRatio || 1) : null;
    if (!region) return;
    try {
      const pdfPage = await this.pdfDoc.getPage(this.config.pageNumber);
      if (seq !== this.sharpSeq) return;
      const r = region.pixelRatio;
      const scale = (pageBox.width / pdfPage.getViewport({ scale: 1 }).width) * r;
      const render = this.pdfRenderService.startRenderRegion(pdfPage, scale,
        { x: region.x * r, y: region.y * r, width: region.width * r, height: region.height * r });
      this.sharpTask = render.task;
      const out = await render.done;
      if (seq !== this.sharpSeq) return;
      this.sharpTask = null;
      this.sharpPatch = { canvas: out, x: region.x / v[0], y: region.y / v[0], w: region.width / v[0], h: region.height / v[0] };
      canvas.requestRenderAll();
    } catch {
      // Cancelled by a newer zoom or pan, or the page could not be drawn: the page image stays
    }
  }

  /** A drag pans: with space held in any tool, or zoomed in on the empty sheet with the Select tool */
  private startPan(canvas: fabric.Canvas, opt: fabric.IEvent): boolean {
    if (!this.isSheetRedline) return false;
    const onEmptySheet = this.activeTool === null && !opt.target && this.zoom > 1;
    if (!this.spaceHeld && !onEmptySheet) return false;
    const e = opt.e as MouseEvent;
    this.panFrom = { x: e.clientX, y: e.clientY };
    canvas.defaultCursor = 'grabbing';
    canvas.setCursor('grabbing');
    return true;
  }

  private movePan(canvas: fabric.Canvas, opt: fabric.IEvent): void {
    const page = this.pageCanvases[0];
    const e = opt.e as MouseEvent;
    if (!this.panFrom || !page) return;
    const dx = e.clientX - this.panFrom.x;
    const dy = e.clientY - this.panFrom.y;
    this.panFrom = { x: e.clientX, y: e.clientY };
    this.setViewport(panViewport(this.viewport, dx, dy, page.width, page.height));
  }

  private endPan(canvas: fabric.Canvas): void {
    this.panFrom = null;
    canvas.defaultCursor = 'grab';
    canvas.setCursor('grab');
  }

  // =====================================================================
  // CANVAS HELPERS
  // =====================================================================

  /**
   * Load an image into fabric with crossOrigin 'anonymous' (required so the
   * canvas isn't tainted and toDataURL works when saving the preview).
   *
   * Gotcha: the photo viewer loads the same URL first as a plain <img> (no
   * crossorigin), which caches a non-CORS response. A subsequent crossOrigin
   * request hits that poisoned cache entry and fails, leaving an empty canvas
   * (so only the blurred CSS backdrop shows). On failure we retry once with a
   * cache-busting param to force a fresh CORS fetch.
   */
  private loadCorsImage(url: string, onLoad: (img: fabric.Image) => void) {
    const attempt = (src: string, retried: boolean) => {
      fabric.Image.fromURL(src, (img) => {
        if (!img?.width || !img?.height) {
          if (!retried) {
            const bust = src + (src.includes('?') ? '&' : '?') + 'markupcors=1';
            attempt(bust, true);
          }
          return;
        }
        onLoad(img);
      }, { crossOrigin: 'anonymous' });
    };
    attempt(url, false);
  }

  private getActiveCanvas(): fabric.Canvas | null {
    if (this.config?.isDocument) {
      return this.pageCanvases[this.activePageIndex]?.fabricCanvas || null;
    }
    return this.singleCanvas;
  }

  private getCanvasContainerWidth(): number {
    const contentPadding = 80;
    // Use totalPages from PDF doc (not pageCanvases.length which may be 0 during init)
    const hasSidebar = this.isSheetRedline ? false : this.pdfDoc ? this.pdfDoc.numPages > 1 : this.pageCanvases.length > 1;
    const sidebarWidth = hasSidebar ? 170 : 0;
    return window.innerWidth - contentPadding - sidebarWidth;
  }

  private calculateCanvasSize(imgWidth: number, imgHeight: number) {
    const headerHeight = 84;
    const contentPadding = 80;
    const sidebarWidth = 170;
    const maxWidth = window.innerWidth - contentPadding - sidebarWidth;
    const maxHeight = window.innerHeight - headerHeight - 20;
    const scaleX = maxWidth / imgWidth;
    const scaleY = maxHeight / imgHeight;
    const scale = Math.min(scaleX, scaleY);
    return {
      width: Math.floor(imgWidth * scale),
      height: Math.floor(imgHeight * scale),
      scale,
    };
  }

  private applyToolToCanvas(canvas: fabric.Canvas) {
    if (!canvas) return;

    canvas.isDrawingMode = false;
    canvas.selection = false;
    canvas.defaultCursor = 'default';

    const tool = this.activeTool;
    const isShapeTool = tool && [
      MarkupTool.RECTANGLE, MarkupTool.CIRCLE, MarkupTool.CLOUD,
      MarkupTool.LINE, MarkupTool.POLYGON,
    ].includes(tool);

    // When no tool is active, enable selection (never on someone else's shared row, W59)
    const selectable = tool === null && !this.rowLocked;
    canvas.forEachObject(obj => {
      obj.selectable = selectable;
      obj.evented = selectable;
    });

    if (tool === null) {
      // Selection mode — also clear any in-progress polygon. Zoomed in, a drag on the empty sheet pans (no marquee).
      this.finalizePolygon(canvas);
      canvas.selection = selectable && this.zoom <= 1;
      canvas.defaultCursor = this.zoom > 1 ? 'grab' : 'default';
    } else if (tool === MarkupTool.FREEHAND) {
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.color = this.activeColor;
      canvas.freeDrawingBrush.width = this.strokeWidth;
    } else if (tool === MarkupTool.TEXT) {
      canvas.defaultCursor = 'text';
    } else if (tool === MarkupTool.POLYGON) {
      canvas.defaultCursor = 'crosshair';
    } else if (isShapeTool) {
      canvas.defaultCursor = 'crosshair';
    }
  }

  private bindCanvasEvents(canvas: fabric.Canvas, pageIndex: number | null) {
    canvas.on('mouse:down', (opt) => {
      // For multi-page, set active page on click
      if (pageIndex !== null) this.setActivePage(pageIndex);
      if (this.startPan(canvas, opt)) return;
      this.onMouseDown(canvas, opt);
    });
    canvas.on('mouse:move', (opt) => {
      if (this.panFrom) { this.movePan(canvas, opt); return; }
      this.onMouseMove(canvas, opt);
    });
    canvas.on('mouse:up', () => {
      if (this.panFrom) { this.endPan(canvas); return; }
      this.onMouseUp(canvas);
    });
    // W76: the toolbar's Delete follows the selection
    const selectionChanged = () => this.cdr.detectChanges();
    canvas.on('selection:created', selectionChanged);
    canvas.on('selection:updated', selectionChanged);
    canvas.on('selection:cleared', selectionChanged);
    canvas.on('mouse:dblclick', () => {
      // Double-click closes polygon
      if (this.activeTool === MarkupTool.POLYGON && this.polygonPoints.length >= 3) {
        this.closePolygon(canvas);
      }
    });
    canvas.on('path:created', (e: fabric.IEvent & { path?: fabric.Path }) => {
      // A resized stroke keeps the thickness it was drawn with
      e.path?.set({ strokeUniform: true });
      this.saveStateForCanvas(canvas);
    });
    canvas.on('object:modified', () => this.saveStateForCanvas(canvas));
  }

  /**
   * @param targetWidth sheet mode only: the canvas width now, so a row saved at another window width
   *   is scaled to fit the page as it is drawn today
   */
  private loadExistingMarkupForCanvas(canvas: fabric.Canvas, markup: Markup | null, callback: () => void, targetWidth?: number) {
    if (markup?.annotation_data?.objects?.length) {
      fabric.util.enlivenObjects(markup.annotation_data.objects, (enlivened) => {
        const ratio = this.isSheetRedline && targetWidth && markup.canvas_width ? targetWidth / markup.canvas_width : 1;
        enlivened.forEach(obj => {
          if (ratio !== 1) {
            obj.set({ left: obj.left * ratio, top: obj.top * ratio, scaleX: obj.scaleX * ratio, scaleY: obj.scaleY * ratio });
            // A uniform stroke ignores scale: it is in the saved canvas's units, so it follows the same ratio
            if (obj.strokeUniform && obj.strokeWidth) obj.set({ strokeWidth: obj.strokeWidth * ratio });
            obj.setCoords();
          }
          canvas.add(obj);
        });
        canvas.renderAll();
        callback();
      }, '');
    } else {
      callback();
    }
  }

  // =====================================================================
  // DRAWING EVENT HANDLERS
  // =====================================================================

  private onMouseDown(canvas: fabric.Canvas, opt: fabric.IEvent) {
    if (this.config.readOnly || !this.activeTool) return;

    const pointer = canvas.getPointer(opt.e);
    this.shapeStartX = pointer.x;
    this.shapeStartY = pointer.y;
    this.shapeEndX = pointer.x;
    this.shapeEndY = pointer.y;

    switch (this.activeTool) {
      case MarkupTool.RECTANGLE:
        this.isDrawingShape = true;
        this.activeShape = new fabric.Rect({
          left: pointer.x, top: pointer.y, width: 0, height: 0,
          stroke: this.activeColor, strokeWidth: this.strokeWidth, strokeUniform: true,
          fill: 'transparent', selectable: false, evented: false,
        });
        canvas.add(this.activeShape);
        break;

      case MarkupTool.CIRCLE:
        this.isDrawingShape = true;
        this.activeShape = new fabric.Ellipse({
          left: pointer.x, top: pointer.y, rx: 0, ry: 0,
          stroke: this.activeColor, strokeWidth: this.strokeWidth, strokeUniform: true,
          fill: 'transparent', selectable: false, evented: false,
        });
        canvas.add(this.activeShape);
        break;

      case MarkupTool.CLOUD:
        this.isDrawingShape = true;
        this.activeShape = this.createCloudPath(pointer.x, pointer.y, 1, 1);
        canvas.add(this.activeShape);
        break;

      case MarkupTool.LINE:
        this.isDrawingShape = true;
        this.activeShape = new fabric.Line(
          [pointer.x, pointer.y, pointer.x, pointer.y],
          { stroke: this.activeColor, strokeWidth: this.strokeWidth, strokeUniform: true, selectable: false, evented: false }
        );
        canvas.add(this.activeShape);
        break;

      case MarkupTool.POLYGON:
        this.addPolygonPoint(canvas, pointer.x, pointer.y);
        break;

      case MarkupTool.TEXT: {
        const text = new fabric.IText('', {
          left: pointer.x, top: pointer.y,
          fontSize: this.fontSize, fill: this.activeColor, fontFamily: 'Montserrat, sans-serif',
        });
        canvas.add(text);
        canvas.setActiveObject(text);
        text.enterEditing();
        this.saveStateForCanvas(canvas);
        break;
      }
    }
  }

  private onMouseMove(canvas: fabric.Canvas, opt: fabric.IEvent) {
    this.trackContextHover(canvas, opt);
    if (!this.isDrawingShape || !this.activeShape) return;
    const pointer = canvas.getPointer(opt.e);
    this.shapeEndX = pointer.x;
    this.shapeEndY = pointer.y;

    switch (this.activeTool) {
      case MarkupTool.RECTANGLE: {
        const rect = this.activeShape as fabric.Rect;
        rect.set({
          left: Math.min(this.shapeStartX, pointer.x),
          top: Math.min(this.shapeStartY, pointer.y),
          width: Math.abs(pointer.x - this.shapeStartX),
          height: Math.abs(pointer.y - this.shapeStartY),
        });
        break;
      }
      case MarkupTool.CIRCLE: {
        const ellipse = this.activeShape as fabric.Ellipse;
        ellipse.set({
          left: Math.min(this.shapeStartX, pointer.x),
          top: Math.min(this.shapeStartY, pointer.y),
          rx: Math.abs(pointer.x - this.shapeStartX) / 2,
          ry: Math.abs(pointer.y - this.shapeStartY) / 2,
        });
        break;
      }
      case MarkupTool.CLOUD: {
        canvas.remove(this.activeShape);
        const w = Math.abs(pointer.x - this.shapeStartX);
        const h = Math.abs(pointer.y - this.shapeStartY);
        this.activeShape = this.createCloudPath(
          Math.min(this.shapeStartX, pointer.x),
          Math.min(this.shapeStartY, pointer.y), w, h
        );
        canvas.add(this.activeShape);
        break;
      }
      case MarkupTool.LINE: {
        (this.activeShape as fabric.Line).set({ x2: pointer.x, y2: pointer.y });
        break;
      }
    }
    canvas.renderAll();
  }

  private onMouseUp(canvas: fabric.Canvas) {
    if (!this.isDrawingShape) return;
    this.isDrawingShape = false;

    // A click or a tiny drag would leave a dot of stroke, not a shape: nothing is drawn
    // Screen px: zoomed in, a small shape is still a shape
    if (this.activeShape && dragExtent(this.activeTool, this.shapeStartX, this.shapeStartY, this.shapeEndX, this.shapeEndY) < MIN_SHAPE_PX / this.zoom) {
      canvas.remove(this.activeShape);
      this.activeShape = null;
      canvas.renderAll();
      return;
    }

    // Add arrowhead if line tool with arrow mode
    if (this.activeTool === MarkupTool.LINE && this.lineArrowMode && this.activeShape) {
      this.addArrowhead(canvas, this.activeShape as fabric.Line);
    }

    if (this.activeShape) this.activeShape.setCoords();
    this.activeShape = null;
    this.saveStateForCanvas(canvas);
  }

  private addArrowhead(canvas: fabric.Canvas, line: fabric.Line) {
    const x1 = line.x1;
    const y1 = line.y1;
    const x2 = line.x2;
    const y2 = line.y2;

    const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
    const headLength = this.strokeWidth * 5;

    const triangle = new fabric.Triangle({
      left: x2,
      top: y2,
      width: headLength,
      height: headLength,
      fill: this.activeColor,
      angle: angle + 90,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
    });

    // Remove line, group line + arrowhead so they select/delete together
    canvas.remove(line);

    // Reset line coords to absolute before grouping
    const groupLine = new fabric.Line([x1, y1, x2, y2], {
      stroke: line.stroke,
      strokeWidth: line.strokeWidth,
      selectable: false,
      evented: false,
    });

    const group = new fabric.Group([groupLine, triangle], {
      selectable: false,
      evented: false,
    });
    canvas.add(group);
    this.activeShape = group;
  }

  // =====================================================================
  // STATE MANAGEMENT
  // =====================================================================

  private saveStateForCanvas(canvas: fabric.Canvas) {
    if (this.skipSaveState) return;

    if (this.config?.isDocument) {
      const pageIndex = this.findPageIndexForCanvas(canvas);
      if (pageIndex === -1) return;
      const page = this.pageCanvases[pageIndex];
      const state = this.getCanvasState(canvas);
      page.undoStack.push(state);
      page.redoStack = [];
      if (page.initialStateRecorded) page.isDirty = true;
      page.initialStateRecorded = true;
    } else {
      this.saveSingleState();
    }
  }

  private saveSingleState() {
    if (this.skipSaveState || !this.singleCanvas) return;
    const state = this.getCanvasState(this.singleCanvas);
    this.singleUndoStack.push(state);
    this.singleRedoStack = [];
    if (this.singleInitialStateRecorded) this.singleIsDirty = true;
    this.singleInitialStateRecorded = true;
  }

  private getCanvasState(canvas: fabric.Canvas): string {
    const json = canvas.toJSON();
    return JSON.stringify({ version: json.version, objects: json.objects });
  }

  private restoreStateForPage(page: PageCanvasState, stateStr: string) {
    if (!page.fabricCanvas) return;
    this.restoreCanvasState(page.fabricCanvas, stateStr);
  }

  private restoreCanvasState(canvas: fabric.Canvas, stateStr: string) {
    const state = JSON.parse(stateStr);
    this.skipSaveState = true;

    const objects = canvas.getObjects().slice();
    objects.forEach(obj => canvas.remove(obj));

    const stateObjects = state.objects || [];
    if (stateObjects.length) {
      fabric.util.enlivenObjects(stateObjects, (enlivened) => {
        enlivened.forEach(obj => canvas.add(obj));
        canvas.renderAll();
        this.skipSaveState = false;
      }, '');
    } else {
      canvas.renderAll();
      this.skipSaveState = false;
    }
  }

  private findPageIndexForCanvas(canvas: fabric.Canvas): number {
    return this.pageCanvases.findIndex(p => p.fabricCanvas === canvas);
  }

  // =====================================================================
  // SAVE LOGIC
  // =====================================================================

  private saveSingleImage() {
    this.singleCanvas.discardActiveObject();
    this.singleCanvas.renderAll();

    const json = this.singleCanvas.toJSON();
    const annotationData = { version: json.version, objects: json.objects };
    const payload: any = {
      annotation_data: annotationData,
      canvas_width: this.singleCanvasWidth,
      canvas_height: this.singleCanvasHeight,
      attachment_id: this.config.attachmentId,
      page_number: 1,
    };

    const saveObs = this.singleMarkup
      ? this.markupService.update(this.singleMarkup.id, payload)
      : this.markupService.create(payload);

    saveObs.subscribe({
      next: (markup: Markup) => {
        this.singleMarkup = markup;
        this.singleIsDirty = false;
        this.markSaved(null);
        this.uploadPreviewForCanvas(this.singleCanvas, markup, this.singleBackgroundScale);
      },
      error: (err) => {
        console.error('Failed to save markup:', err);
        this.saveError = 'Failed to save. Please try again.';
        this.isSaving = false;
      },
    });
  }

  private async saveAllPages() {
    // Save all dirty pages, then emit result from the active page
    const dirtyPages = this.pageCanvases
      .map((p, i) => ({ page: p, index: i }))
      .filter(({ page }) => page.isDirty && page.fabricCanvas);

    try {
      for (const { page, index } of dirtyPages) {
        await this.savePageMarkup(page, index);
      }

      // If no dirty pages, save active page anyway (user clicked Save)
      if (dirtyPages.length === 0) {
        const activePage = this.pageCanvases[this.activePageIndex];
        if (activePage?.fabricCanvas) {
          await this.savePageMarkup(activePage, this.activePageIndex);
        }
      }

      this.isSaving = false;
      // For multi-page PDFs, always emit page 1's preview as the thumbnail
      // (the attachment thumbnail in lists/carousel should show page 1, not whatever page was active)
      const page1 = this.pageCanvases[0];
      const emitPage = page1?.markup ? page1 : this.pageCanvases.find(p => p.markup);
      if (emitPage?.markup) {
        const dataUrl = page1?.fabricCanvas?.toDataURL({ format: 'jpeg', quality: 0.85 }) || '';
        this.saved.emit({ markup: emitPage.markup, previewDataUrl: dataUrl });
      } else {
        this.saved.emit({ markup: null, previewDataUrl: '' } as any);
      }
    } catch (err) {
      console.error('Failed to save pages:', err);
      this.saveError = 'Failed to save. Please try again.';
      this.isSaving = false;
    }
  }

  private autoSavePage(page: PageCanvasState, pageIndex: number) {
    // Fire-and-forget save when a dirty page scrolls out of view
    this.savePageMarkup(page, pageIndex).then(() => {
      const thumb = page.fabricCanvas?.toDataURL({ format: 'jpeg', quality: 0.85 });
      if (thumb) this.pageThumbnails[pageIndex] = thumb;
      this.cdr.detectChanges();
    }).catch(err => {
      console.error(`Auto-save failed for page ${pageIndex + 1}:`, err);
    });
  }

  private savePageMarkup(page: PageCanvasState, pageIndex: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const canvas = page.fabricCanvas;
      canvas.discardActiveObject();
      canvas.renderAll();

      const json = canvas.toJSON();
      const annotationData = { version: json.version, objects: json.objects };
      const payload: any = {
        annotation_data: annotationData,
        canvas_width: page.width,
        canvas_height: page.height,
        attachment_id: this.config.attachmentId,
        page_number: pageIndex + 1,
      };

      const saveObs = page.markup
        ? this.markupService.update(page.markup.id, payload)
        : this.markupService.create(payload);

      saveObs.subscribe({
        next: (markup: Markup) => {
          page.markup = markup;
          page.isDirty = false;
          this.markSaved(page);

          // Upload preview
          const multiplier = page.backgroundScale > 0 && page.backgroundScale < 1
            ? 1 / page.backgroundScale : 1;
          const fullResDataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.92, multiplier });
          const blob = this.dataUrlToBlob(fullResDataUrl);

          const thumb = canvas.toDataURL({ format: 'jpeg', quality: 0.85 });
          this.pageThumbnails[pageIndex] = thumb;

          this.markupService.uploadPreview(markup.id, blob).subscribe({
            next: () => resolve(),
            error: () => resolve(), // Continue even if preview upload fails
          });
        },
        error: reject,
      });
    });
  }

  private uploadPreviewForCanvas(canvas: fabric.Canvas, markup: Markup, bgScale: number) {
    const dataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.85 });
    const multiplier = bgScale > 0 && bgScale < 1 ? 1 / bgScale : 1;
    const fullResDataUrl = canvas.toDataURL({ format: 'jpeg', quality: 0.92, multiplier });
    const blob = this.dataUrlToBlob(fullResDataUrl);

    const result: MarkupSaveResult = { markup, previewDataUrl: dataUrl };

    this.markupService.uploadPreview(markup.id, blob).subscribe({
      next: (resp: any) => {
        if (resp?.data) {
          result.updatedPreviewUrl = resp.data.preview_url;
          result.updatedThumbUrl = resp.data.thumb_url;
        }
        if (resp?.marked_up_url) result.markedUpUrl = resp.marked_up_url;
        this.isSaving = false;
        this.saved.emit(result);
      },
      error: () => {
        this.isSaving = false;
        this.saved.emit(result);
      },
    });
  }

  // --- Drawing sheet redline save ---

  private saveSheetRedline() {
    const page = this.pageCanvases[0];
    const canvas = page?.fabricCanvas;
    if (!canvas) {
      this.isSaving = false;
      return;
    }
    canvas.discardActiveObject();
    canvas.renderAll();

    const json = canvas.toJSON();
    const body = {
      annotation_data: { version: json.version, objects: json.objects },
      canvas_width: page.width,
      canvas_height: page.height,
    };
    if (this.config.deferSave && !this.config.linkUuid) {
      this.holdSheetRedline(page, body);
      return;
    }
    // 7.2: the record's open as-built mark, emptied: it is deleted (the as-built card's rule; the user confirmed)
    if (this.isLinkRedline && this.isAsBuiltMode && page.markup?.id && !body.annotation_data.objects?.length) {
      this.deleteRecordAsBuilt(page);
      return;
    }
    if (this.isAsBuiltMode && !page.markup?.id) {
      this.recordAsBuilt(page, body);
      return;
    }
    const slot: RedlineSlot = { attachment_id: this.config.attachmentId, page_number: this.config.pageNumber, visibility: this.redlineVisibility };
    if (this.config.linkUuid) slot.link_uuid = this.config.linkUuid;

    this.markupService.saveRedline(page.markup, body, slot).subscribe({
      next: (markup: Markup) => {
        page.markup = markup;
        page.isDirty = false;
        this.markSaved(page);
        this.redlineRows = [...this.redlineRows.filter(r => r.id !== markup.id), markup];
        this.uploadSheetPreview(page, markup);
      },
      error: (err) => {
        this.isSaving = false;
        // Keep the canvas: the user chooses Reload or Keep mine. A slot filled meanwhile is the
        // same choice as a stale version; nothing is written over another user's row silently.
        if (err instanceof MarkupConflictError || err instanceof MarkupExistsError) {
          this.openConflict(err.current);
          return;
        }
        if (err instanceof MarkupLockedError) {
          // 7.2: the record's as-built mark was reviewed meanwhile: the edit becomes a new open mark (P17)
          if (this.isLinkRedline && this.isAsBuiltMode && page.markup?.kind === 'as_built') {
            this.asBuiltSource = page.markup;
            page.markup = null;
            this.isSaving = true;
            this.recordAsBuilt(page, body);
            return;
          }
          this.saveError = err.message;
          return;
        }
        console.error('Failed to save markup:', err);
        this.saveError = this.drawings.failureMessage(err?.error?.code, this.drawings.errorMessage(err));
      },
    });
  }

  /** 7.2: a record's own open as-built mark, cleared and saved: deleted, as from its card (author, while open) */
  private deleteRecordAsBuilt(page: PageCanvasState) {
    const row = page.markup;
    this.markupService.delete(row.id).subscribe({
      next: () => {
        const empty = new Markup({ annotation_data: { objects: [] }, canvas_width: page.width, canvas_height: page.height,
          attachment_id: this.config.attachmentId, page_number: this.config.pageNumber, visibility: 'shared', kind: 'as_built' });
        this.redlineRows = this.redlineRows.filter(r => r.id !== row.id);
        page.markup = null;
        page.isDirty = false;
        this.markSaved(page);
        this.isSaving = false;
        this.refreshRedlineLayers();
        this.saved.emit({ markup: empty, previewDataUrl: this.sheetPreviewDataUrl(page) });
      },
      error: (err) => {
        this.isSaving = false;
        console.error('Failed to delete the as-built mark:', err);
        this.saveError = err instanceof MarkupLockedError
          ? 'This as-built mark has been reviewed, so it stays. Delete it from its card on the sheet.'
          : this.drawings.failureMessage(err?.error?.code, this.drawings.errorMessage(err));
      },
    });
  }

  /**
   * One new as-built row per save. Its client_uuid is kept until the save lands (row and preview), so a retry after a
   * network failure returns the row the first attempt made rather than adding a second (R33).
   */
  private recordAsBuilt(page: PageCanvasState, body: RedlineBody) {
    if (!body.annotation_data.objects?.length) {
      this.isSaving = false;
      this.saveError = this.asBuiltSource
        ? 'This as-built mark has been reviewed, so it stays. Delete it from its card on the sheet.'
        : 'Draw the as-built mark on the sheet, then Save';
      return;
    }
    this.asBuiltClientUuid = this.asBuiltClientUuid || uuidv4();
    const slot: Pick<RedlineSlot, 'attachment_id' | 'page_number' | 'link_uuid'> = { attachment_id: this.config.attachmentId, page_number: this.config.pageNumber };
    // 7.2: a record's as-built mark is also tagged to its link
    if (this.config.linkUuid) slot.link_uuid = this.config.linkUuid;
    this.markupService.createAsBuilt(body, slot, this.asBuiltClientUuid)
      .pipe(
        // A retry whose first POST landed (its answer lost) gets that row back with the strokes it was sent then; when
        // more were drawn since, they go onto the same row before its preview is made.
        switchMap((markup: Markup) => sameStrokes(markup.annotation_data, body.annotation_data)
          ? of(markup)
          : this.markupService.update(markup.id, { ...body, expected_version: markup.version })),
      )
      .subscribe({
        next: (markup: Markup) => {
          // Until the preview lands, Save again updates this row and Retry re-sends its preview.
          if (this.asBuiltSource) {
            // P17: the locked mark stays (faded under the new one); this one is the record's now
            this.asBuiltSource = null;
            this.asBuiltCopied = true;
          }
          page.markup = markup;
          page.isDirty = false;
          this.markSaved(page);
          this.redlineRows = [...this.redlineRows.filter(r => r.id !== markup.id), markup];
          this.refreshRedlineLayers();
          this.uploadSheetPreview(page, markup);
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Failed to save the as-built mark:', err);
          // R51: that id can't make a row any more; the next Save sends a fresh one
          if (err?.error?.code === 'MARKUP_CLIENT_UUID_REUSED') {
            this.asBuiltClientUuid = null;
          }
          // The follow-up PUT can meet a lock or someone else's change: say which, not the generic error.
          this.saveError = err instanceof MarkupLockedError || err instanceof MarkupConflictError
            ? err.message : this.drawings.failureMessage(err?.error?.code, this.drawings.errorMessage(err));
        },
      });
  }

  /** The link is not saved yet: hand the strokes and their preview back; the caller posts them after the record saves */
  private holdSheetRedline(page: PageCanvasState, body: RedlineBody) {
    const markup = new Markup({
      ...body, attachment_id: this.config.attachmentId, page_number: this.config.pageNumber, visibility: 'shared',
      // 7.2: the held strokes settle as this kind once the record saves
      ...(this.isAsBuiltMode ? { kind: 'as_built' } : {}),
    });
    page.markup = markup;
    page.isDirty = false;
    this.markSaved(page);
    this.isSaving = false;
    this.saved.emit({ markup, previewDataUrl: this.sheetPreviewDataUrl(page) });
  }

  /** Strokes only, as a transparent PNG */
  private sheetPreviewDataUrl(page: PageCanvasState): string {
    const canvas = page.fabricCanvas;
    const background = canvas.backgroundImage;
    canvas.backgroundImage = null;
    const dataUrl = canvas.toDataURL({ format: 'png', multiplier: this.sheetPreviewMultiplier(page) });
    canvas.backgroundImage = background;
    canvas.renderAll();
    return dataUrl;
  }

  /** The preview upload failed after the row saved: upload only the preview again. */
  retryPreview() {
    const failed = this.failedPreview;
    if (!failed || this.isSaving || this.conflict) return;
    this.isSaving = true;
    this.saveError = null;
    this.failedPreview = null;
    this.uploadSheetPreview(failed.page, failed.markup);
  }

  /**
   * Strokes only, as a transparent PNG, so the viewer and the PDF export can stack several layers on the page. Without
   * it the viewer and the export skip the row, so a failed upload is not a save: the strokes stay, no `saved`, and
   * Retry sends the preview again.
   */
  private uploadSheetPreview(page: PageCanvasState, markup: Markup) {
    const dataUrl = this.sheetPreviewDataUrl(page);
    const result: MarkupSaveResult = { markup, previewDataUrl: dataUrl };
    this.markupService.uploadPreview(markup.id, this.dataUrlToBlob(dataUrl)).subscribe({
      next: (resp: { data?: { preview_url?: string } }) => {
        if (resp?.data?.preview_url) {
          result.updatedPreviewUrl = resp.data.preview_url;
          markup.preview_url = resp.data.preview_url;
        }
        this.isSaving = false;
        if (this.recordsAsBuilt) {
          // That mark is recorded: the canvas clears for the next deviation, which gets its own row.
          this.asBuiltClientUuid = null;
          // W63: say so; the mark now shows under the cleared canvas, which otherwise looks as if nothing was saved
          this.asBuiltRecorded = true;
          this.loadRowIntoPage(page, null);
          this.refreshRedlineLayers();
        }
        // W76: the sheet's field note, edited from a record's editor, is not the record's markup
        if (!this.sheetRedlineOnly) this.saved.emit(result);
      },
      error: (err) => {
        console.error('Failed to upload the redline preview:', err);
        this.isSaving = false;
        this.failedPreview = { page, markup };
        this.saveError = 'Saved, but the preview failed to upload. Retry.';
        this.cdr.detectChanges();
      },
    });
  }

  private openConflict(current: Markup) {
    const active = document.activeElement as HTMLElement | null;
    this.conflictReturnFocus = active && this.host.nativeElement.contains(active) ? active : null;
    this.conflict = current;
    this.cdr.detectChanges();
    this.conflictReloadRef?.nativeElement.focus();
  }

  /** Back to where the user was (Save, usually); the canvas area when that is now disabled */
  private restoreFocusAfterConflict() {
    const previous = this.conflictReturnFocus;
    this.conflictReturnFocus = null;
    const usable = previous?.isConnected && !(previous as HTMLButtonElement).disabled;
    (usable ? previous : this.scrollContainerRef?.nativeElement)?.focus();
  }

  private loadRowIntoPage(page: PageCanvasState, row: Markup | null, then?: () => void) {
    const canvas = page.fabricCanvas;
    page.markup = row;
    canvas.getObjects().slice().forEach(obj => canvas.remove(obj));
    if (row?.can_edit === false) {
      this.activeTool = null;
    }
    this.loadExistingMarkupForCanvas(canvas, row, () => {
      canvas.renderAll();
      page.undoStack = [this.getCanvasState(canvas)];
      page.redoStack = [];
      page.isDirty = false;
      page.savedState = undefined;
      this.applyToolToCanvas(canvas);
      then?.();
    }, page.width);
  }

  /**
   * Under the edited row: the page's untagged shared rows (carried copies and as-built marks included), not every
   * record's tagged layer (W25)
   */
  private refreshRedlineLayers() {
    const editing = this.pageCanvases[0]?.markup?.id;
    const copied = this.asBuiltSource?.id;
    // Every as-built mark stays as faded context, a record's too (7.2), except the one on the canvas
    this.redlineLayers = this.redlineRows.filter(r => r.preview_url && r.id !== editing && r.id !== copied && r.visibility === 'shared'
      && (!r.link_uuid || r.kind === 'as_built'));
  }

  /** W76: in Select mode, the faded context row under the pointer (by its strokes' box) gets its card */
  private trackContextHover(canvas: fabric.Canvas, opt: fabric.IEvent): void {
    const page = this.pageCanvases[0];
    if (!this.isSheetRedline || this.activeTool !== null || !page?.width || !page.height || canvas !== page.fabricCanvas) return;
    const p = canvas.getPointer(opt.e);
    const x = p.x / page.width;
    const y = p.y / page.height;
    const hit = this.redlineLayers
      .map(row => ({ row, box: this.contextBox(row) }))
      .find(m => !!m.box && x >= m.box.x0 && x <= m.box.x1 && y >= m.box.y0 && y <= m.box.y1) ?? null;
    if (hit && (!this.hoveredLayer || hit.row === this.hoveredLayer.row)) {
      this.keepContextCard();
      if (!this.hoveredLayer) {
        this.hoveredLayer = hit;
        this.cdr.detectChanges();
      }
    } else if (hit) {
      // Crossing another row on the way to the open card must not swap the card out from under the pointer: the new
      // row takes over only after the pointer rests on it
      this.releaseContextCard();
      if (this.hoverSwitchTimer) clearTimeout(this.hoverSwitchTimer);
      this.hoverSwitchTimer = setTimeout(() => {
        this.hoverSwitchTimer = null;
        this.keepContextCard();
        this.hoveredLayer = hit;
        this.cdr.detectChanges();
      }, 250);
    } else if (this.hoveredLayer) {
      this.releaseContextCard();
    }
  }

  contextBox(row: Markup): MarkBox | null {
    if (!this.contextBoxes.has(row)) {
      this.contextBoxes.set(row, markupBBox(row.annotation_data, row.canvas_width, row.canvas_height));
    }
    return this.contextBoxes.get(row);
  }

  /** Review M1: keyboard route: the row's hotspot took focus, so its card shows (Enter on it edits an editable note) */
  focusContext(row: Markup): void {
    const box = this.contextBox(row);
    if (!box) return;
    this.keepContextCard();
    this.hoveredLayer = { row, box };
  }

  /** The pointer is on the row or its card: the card stays (and a pending swap to another row is off) */
  keepContextCard(): void {
    if (this.hoverHideTimer) clearTimeout(this.hoverHideTimer);
    this.hoverHideTimer = null;
    if (this.hoverSwitchTimer) clearTimeout(this.hoverSwitchTimer);
    this.hoverSwitchTimer = null;
  }

  /** Off the row and its card: it goes after a moment, so the pointer can travel to Edit sheet redline */
  releaseContextCard(): void {
    if (this.hoverHideTimer) return;
    this.hoverHideTimer = setTimeout(() => {
      this.hoverHideTimer = null;
      this.hoveredLayer = null;
      this.cdr.detectChanges();
    }, 300);
  }

  /** "Sheet redline · Jack Smith · Oct 10, 2026" (an as-built mark says so) */
  contextCopy(row: Markup): string {
    const when = row.updated_at || row.created_at;
    const date = when ? new Date(when).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const kind = row.kind === 'as_built' ? 'As-built mark' : row.carried_from_uuid ? 'Carried sheet redline' : 'Sheet redline';
    return [kind, authorName(row), date].filter(Boolean).join(' · ');
  }

  /** The sheet's own shared field note can be edited from here (the caller may edit it; as-built marks are reviewed elsewhere) */
  canEditContext(row: Markup): boolean {
    return row.kind !== 'as_built' && row.visibility === 'shared' && row.can_edit !== false;
  }

  /**
   * W76: "Edit sheet redline": the editor leaves the record's row for the hovered sheet redline itself (review I2: a
   * carried copy is that row, not the page's own note). From a record it switches in place (unsaved record strokes ask
   * first) and its saves no longer reach the record's host.
   */
  editSheetRedline(row: Markup | null = this.hoveredLayer?.row ?? null): void {
    const page = this.pageCanvases[0];
    if (!page?.fabricCanvas || this.isSaving || this.conflict) return;
    const go = () => {
      this.hoveredLayer = null;
      if (this.isLinkRedline) {
        this.config = { ...this.config, linkUuid: null, deferSave: false, existingMarkup: undefined };
        this.sheetRedlineOnly = true;
      }
      this.redlineKind = 'note';
      this.redlineVisibility = 'shared';
      this.asBuiltSource = null;
      this.asBuiltCopied = false;
      this.loadRowIntoPage(page, row ?? MarkupService.pickPageRedline(this.redlineRows, 'shared'));
      this.refreshRedlineLayers();
      this.cdr.detectChanges();
    };
    if (!this.hasChanges) {
      go();
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Unsaved changes', 'There are unsaved changes.<br/>Are you sure you want to discard?'),
    }).afterClosed().subscribe(discard => {
      if (discard) go();
    });
  }

  /** pdf.js scale for the sheet page: 1.5 like other documents, capped at MAX_SHEET_RENDER_PIXELS */
  private sheetRenderScale(pageInfo: { width: number; height: number }): number {
    // pageInfo is measured at scale 1.5
    const cap = 1.5 * Math.sqrt(MAX_SHEET_RENDER_PIXELS / (pageInfo.width * pageInfo.height));
    return Math.min(1.5, cap);
  }

  private sheetPreviewMultiplier(page: PageCanvasState): number {
    const full = page.backgroundScale > 0 && page.backgroundScale < 1 ? 1 / page.backgroundScale : 1;
    return Math.min(full, Math.sqrt(MAX_SHEET_RENDER_PIXELS / (page.width * page.height)));
  }

  // =====================================================================
  // CLOUD SHAPE HELPER
  // =====================================================================

  private createCloudPath(left: number, top: number, width: number, height: number): fabric.Path {
    const w = Math.max(width, 20);
    const h = Math.max(height, 20);
    const bumpCountH = Math.max(3, Math.floor(w / 30));
    const bumpCountV = Math.max(2, Math.floor(h / 30));
    const bumpWidthH = w / bumpCountH;
    const bumpHeightV = h / bumpCountV;

    let path = `M ${left} ${top + h / 2}`;
    for (let i = bumpCountV; i > 0; i--) {
      const y2 = top + ((i - 0.5) * bumpHeightV);
      const y3 = top + ((i - 1) * bumpHeightV);
      path += ` Q ${left - bumpHeightV * 0.4} ${y2}, ${left} ${y3}`;
    }
    for (let i = 0; i < bumpCountH; i++) {
      const x2 = left + ((i + 0.5) * bumpWidthH);
      const x3 = left + ((i + 1) * bumpWidthH);
      path += ` Q ${x2} ${top - bumpWidthH * 0.4}, ${x3} ${top}`;
    }
    for (let i = 0; i < bumpCountV; i++) {
      const y2 = top + ((i + 0.5) * bumpHeightV);
      const y3 = top + ((i + 1) * bumpHeightV);
      path += ` Q ${left + w + bumpHeightV * 0.4} ${y2}, ${left + w} ${y3}`;
    }
    for (let i = bumpCountH; i > 0; i--) {
      const x2 = left + ((i - 0.5) * bumpWidthH);
      const x3 = left + ((i - 1) * bumpWidthH);
      path += ` Q ${x2} ${top + h + bumpWidthH * 0.4}, ${x3} ${top + h}`;
    }
    path += ' Z';

    return new fabric.Path(path, {
      stroke: this.activeColor, strokeWidth: this.strokeWidth, strokeUniform: true,
      fill: 'transparent', selectable: false, evented: false,
    });
  }

  // =====================================================================
  // POLYGON TOOL
  // =====================================================================

  private addPolygonPoint(canvas: fabric.Canvas, x: number, y: number) {
    // If close to first point and we have 3+ points, close the polygon
    if (this.polygonPoints.length >= 3) {
      const first = this.polygonPoints[0];
      const dist = Math.sqrt((x - first.x) ** 2 + (y - first.y) ** 2);
      if (dist < 15 / this.zoom) {
        this.closePolygon(canvas);
        return;
      }
    }

    this.polygonPoints.push({ x, y });

    // Draw vertex dot
    const dot = new fabric.Circle({
      left: x - 4 / this.zoom, top: y - 4 / this.zoom, radius: 4 / this.zoom,
      fill: this.activeColor, selectable: false, evented: false,
    });
    canvas.add(dot);
    this.polygonDots.push(dot);

    // Draw line from previous point
    if (this.polygonPoints.length > 1) {
      const prev = this.polygonPoints[this.polygonPoints.length - 2];
      const line = new fabric.Line([prev.x, prev.y, x, y], {
        stroke: this.activeColor, strokeWidth: this.strokeWidth,
        selectable: false, evented: false,
      });
      canvas.add(line);
      this.polygonLines.push(line);
    }

    canvas.renderAll();
  }

  private closePolygon(canvas: fabric.Canvas) {
    if (this.polygonPoints.length < 3) return;

    // Remove temporary lines and dots
    this.polygonLines.forEach(l => canvas.remove(l));
    this.polygonDots.forEach(d => canvas.remove(d));

    // Create the final polygon
    const points = this.polygonPoints.map(p => new fabric.Point(p.x, p.y));
    const polygon = new fabric.Polygon(points, {
      stroke: this.activeColor,
      strokeWidth: this.strokeWidth,
      strokeUniform: true,
      fill: 'transparent',
      selectable: false,
      evented: false,
    });
    canvas.add(polygon);
    canvas.renderAll();

    // Reset polygon state
    this.polygonPoints = [];
    this.polygonLines = [];
    this.polygonDots = [];
    this.saveStateForCanvas(canvas);
  }

  private finalizePolygon(canvas: fabric.Canvas) {
    if (!canvas || this.polygonPoints.length === 0) return;
    if (this.polygonPoints.length >= 3) {
      this.closePolygon(canvas);
    } else {
      // Not enough points — clean up
      this.polygonLines.forEach(l => canvas.remove(l));
      this.polygonDots.forEach(d => canvas.remove(d));
      this.polygonPoints = [];
      this.polygonLines = [];
      this.polygonDots = [];
      canvas.renderAll();
    }
  }

  // =====================================================================
  // UTILITIES
  // =====================================================================

  private dataUrlToBlob(dataUrl: string): Blob {
    const byteString = atob(dataUrl.split(',')[1]);
    const mimeType = dataUrl.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: mimeType });
  }
}

